import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import type { DateAvailabilitySummary } from '@lov/shared';
import { AVAILABILITY_COLORS, toISTDateString, formatISTLocale } from '@lov/shared';

interface DatePickerProps {
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  daysToShow?: number;
  dateAvailability?: DateAvailabilitySummary[];
  isLoadingAvailability?: boolean;
}

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  overflowX: 'auto',
  padding: theme.spacing(1, 0),
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const DateChip = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' &&
    prop !== 'isToday' &&
    prop !== 'isUnavailable' &&
    prop !== 'availabilityColor',
})<{
  isSelected?: boolean;
  isToday?: boolean;
  isUnavailable?: boolean;
  availabilityColor?: string;
}>(({ theme, isSelected, isToday, isUnavailable, availabilityColor }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 64,
  height: 72,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  cursor: isUnavailable ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  opacity: isUnavailable ? 0.6 : 1,
  backgroundColor: isSelected
    ? theme.palette.primary.main
    : isToday
      ? theme.palette.action.selected
      : theme.palette.background.paper,
  border: `2px solid ${
    isSelected ? theme.palette.primary.main : availabilityColor || theme.palette.divider
  }`,
  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isUnavailable
      ? theme.palette.background.paper
      : isSelected
        ? theme.palette.primary.dark
        : theme.palette.action.hover,
  },
}));

const DayName = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'uppercase',
});

const DayNumber = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 600,
});

const MonthName = styled(Typography)({
  fontSize: '0.625rem',
  opacity: 0.7,
});

const AvailabilityDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color,
  boxShadow: `0 0 4px ${color}`,
}));

function formatDateString(date: Date): string {
  // Use IST date string to avoid timezone conversion issues
  return toISTDateString(date);
}

function getDayName(date: Date): string {
  return formatISTLocale(date, { weekday: 'short' });
}

function getMonthName(date: Date): string {
  return formatISTLocale(date, { month: 'short' });
}

export function DatePicker({
  selectedDate,
  onSelectDate,
  daysToShow = 14,
  dateAvailability = [],
  isLoadingAvailability = false,
}: DatePickerProps) {
  const dates = useMemo(() => {
    const result: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      result.push(date);
    }
    return result;
  }, [daysToShow]);

  const todayString = formatDateString(new Date());

  // Create a map for quick availability lookup
  const availabilityMap = useMemo(() => {
    const map = new Map<string, DateAvailabilitySummary>();
    dateAvailability.forEach((item) => {
      map.set(item.date, item);
    });
    return map;
  }, [dateAvailability]);

  const getAvailabilityInfo = (dateString: string) => {
    const availability = availabilityMap.get(dateString);
    if (!availability) return null;

    const { level, availableSlots, isClosed } = availability;

    if (isClosed || level === 'closed') {
      return {
        color: AVAILABILITY_COLORS.unavailable,
        tooltip: 'Closed',
        isUnavailable: true,
      };
    }

    if (level === 'unavailable') {
      return {
        color: AVAILABILITY_COLORS.unavailable,
        tooltip: 'Unavailable',
        isUnavailable: true,
      };
    }

    const tooltipMap: Record<string, string> = {
      full: `Available - ${availableSlots} slots`,
      high: `Available - ${availableSlots} slots`,
      medium: `Limited - ${availableSlots} slots`,
      low: `Limited - ${availableSlots} slots`,
    };

    return {
      color: AVAILABILITY_COLORS[level as keyof typeof AVAILABILITY_COLORS],
      tooltip: tooltipMap[level] || `${availableSlots} slots available`,
      isUnavailable: false,
    };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Select Date
        </Typography>
        {isLoadingAvailability && <CircularProgress size={16} thickness={4} />}
      </Box>
      <ScrollContainer>
        {dates.map((date) => {
          const dateString = formatDateString(date);
          const isSelected = selectedDate === dateString;
          const isToday = dateString === todayString;
          const availabilityInfo = getAvailabilityInfo(dateString);

          const dateChip = (
            <DateChip
              key={dateString}
              isSelected={isSelected}
              isToday={isToday}
              isUnavailable={availabilityInfo?.isUnavailable}
              availabilityColor={availabilityInfo?.color}
              onClick={() => {
                if (!availabilityInfo?.isUnavailable) {
                  onSelectDate(dateString);
                }
              }}
              role="button"
              aria-pressed={isSelected}
              aria-label={`Select ${formatISTLocale(date, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}${availabilityInfo ? ` - ${availabilityInfo.tooltip}` : ''}`}
              aria-disabled={availabilityInfo?.isUnavailable}
            >
              {availabilityInfo && <AvailabilityDot color={availabilityInfo.color} />}
              <DayName>{isToday ? 'Today' : getDayName(date)}</DayName>
              <DayNumber>{date.getDate()}</DayNumber>
              <MonthName>{getMonthName(date)}</MonthName>
            </DateChip>
          );

          // Wrap with Tooltip if we have availability info
          if (availabilityInfo) {
            return (
              <Tooltip key={dateString} title={availabilityInfo.tooltip} arrow>
                {dateChip}
              </Tooltip>
            );
          }

          return dateChip;
        })}
      </ScrollContainer>
    </Box>
  );
}
